import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Search, Users, MessageSquare, ArrowLeft, Pin, Lock, CalendarDays, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/site/SiteBits";
import { usePageMeta, useLive } from "@/lib/site";
import { useAuth } from "@/context/AuthContext";
import { stageApi, compact, timeAgo } from "@/lib/stageApi";
import { circlesApi } from "@/lib/stageExtraApi";
import { CreatorAvatar, VerifiedMark } from "@/components/stage/PostCard";
import { CircleClassroom, CircleChallenges, CircleLeaderboard } from "@/components/stage/CircleTabs";
import { CircleMessageTools } from "@/pages/stage/Social";

function price(access) {
  if (!access || access.model !== "paid") return "Free";
  const m = access.price_pence ? `£${(access.price_pence / 100).toFixed(2)}/mo` : null;
  const y = access.annual_price_pence ? `£${(access.annual_price_pence / 100).toFixed(2)}/yr` : null;
  return [m, y].filter(Boolean).join(" · ") || "Paid";
}

export function CircleCard({ c }) {
  return (
    <Link to={`/circles/${c.id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card card-hover" data-testid={`circle-${c.id}`}>
      <div className="relative h-28 bg-muted">
        {c.cover_url && <img src={c.cover_url} alt="" loading="lazy" className="h-full w-full object-cover" />}
        <div className="absolute -bottom-6 left-4 rounded-2xl border-4 border-card bg-card"><CreatorAvatar author={{ avatar_url: c.avatar_url, display_name: c.name }} size={48} className="!rounded-xl" /></div>
        <span className="absolute right-3 top-3 rounded-full bg-background/90 px-2.5 py-1 text-[11px] font-semibold">{price(c.access)}</span>
      </div>
      <div className="px-4 pb-4 pt-8">
        <h3 className="truncate font-semibold">{c.name}</h3>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.description}</p>
        <p className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Users size={12} /> {compact(c.stats?.members)}</span>
          <span className="inline-flex items-center gap-1"><MessageSquare size={12} /> {compact(c.stats?.messages)}</span>
          <span className="ml-auto truncate">by {c.owner?.display_name || c.owner?.handle}</span>
        </p>
      </div>
    </Link>
  );
}

function StartCircle() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const create = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 3) { toast.error("Names need at least 3 characters."); return; }
    setBusy(true);
    try { const c = await circlesApi.create({ name: f.name.trim(), description: f.description.trim() }); toast.success("Your Circle is live."); navigate("/circles/" + c.id + "/manage"); }
    catch (err) { toast.error(err.message || "Couldn't create the Circle."); } finally { setBusy(false); }
  };
  if (!open) return <div className="mb-6 flex justify-end"><Button className="rounded-full" onClick={() => setOpen(true)} data-testid="start-circle"><Plus size={16} /> Start a Circle</Button></div>;
  return (
    <form onSubmit={create} className="mb-8 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="start-circle-form">
      <h2 className="text-lg font-bold">Start a Circle</h2>
      <p className="text-sm text-muted-foreground">Free to start. Add channels, a classroom, challenges and paid membership once it's live.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_2fr]">
        <input value={f.name} maxLength={48} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Circle name" aria-label="Circle name" className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        <input value={f.description} maxLength={600} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What's it for?" aria-label="What's it for" className="rounded-xl border border-border bg-background px-3 py-2 text-sm" />
      </div>
      <div className="mt-3 flex gap-2"><Button type="submit" className="rounded-full" disabled={busy}>Create</Button><Button type="button" variant="ghost" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button></div>
    </form>
  );
}

export function Circles() {
  usePageMeta("Circles", "Communities run by creators on Fable + Stage — classrooms, clubs and inner circles.");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "popular";
  const [search, setSearch] = useState(q);
  const config = useLive(() => stageApi.config(), []);
  const [data, setData] = useState(undefined);
  const mine = useLive(() => (user ? circlesApi.mine() : Promise.resolve({ items: [] })), [user?.id]);

  useEffect(() => {
    let alive = true;
    setData(undefined);
    circlesApi.list({ q: q || undefined, category: category || undefined, sort, limit: 30 }).then((d) => alive && setData(d)).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [q, category, sort, user?.id]);
  const set = (patch) => { const n = new URLSearchParams(params); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); setParams(n, { replace: true }); };
  const cats = useMemo(() => config?.categories || [], [config]);
  const items = data?.items || [];

  return (
    <div>
      <PageHeader title="Circles." lede="Creators' own communities — chat channels, classrooms, programmes and meetups, free or paid, all on one Fable + account.">
        <form onSubmit={(e) => { e.preventDefault(); set({ q: search.trim() }); }} className="flex max-w-lg items-center gap-2" role="search">
          <label className="sr-only" htmlFor="circles-q">Search Circles</label>
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input id="circles-q" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Circles" data-testid="circles-search" className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <Button type="submit" className="rounded-full px-5">Search</Button>
        </form>
      </PageHeader>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {user && <StartCircle />}
        {mine?.items?.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-bold">Your Circles</h2>
            <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{mine.items.map((c) => <CircleCard key={c.id} c={c} />)}</div>
          </section>
        )}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button type="button" onClick={() => set({ category: "" })} aria-pressed={!category} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium ${!category ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>All</button>
            {cats.map((c) => <button key={c.key} type="button" onClick={() => set({ category: category === c.key ? "" : c.key })} aria-pressed={category === c.key} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium ${category === c.key ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>{c.label}</button>)}
          </div>
          <select value={sort} onChange={(e) => set({ sort: e.target.value })} aria-label="Sort" className="rounded-full border border-border bg-card px-3 py-1.5 text-sm" data-testid="circles-sort"><option value="popular">Most members</option><option value="new">Newest</option></select>
        </div>
        {data === undefined ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}</div>
        ) : data === null ? (
          <p className="mt-10 text-center text-muted-foreground">Couldn't load Circles right now.</p>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border p-10 text-center"><p className="font-semibold">No Circles match that</p><Button variant="outline" className="mt-4 rounded-full" onClick={() => { setSearch(""); setParams({}, { replace: true }); }}>Clear</Button></div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="circles-grid">{items.map((c) => <CircleCard key={c.id} c={c} />)}</div>
        )}
      </div>
    </div>
  );
}

function Composer({ cid, chan, onSent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try { const m = await circlesApi.send(cid, chan, t); setText(""); onSent(m); } catch (err) { toast.error(err.message || "Couldn't send."); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="mt-4 flex items-end gap-2" data-testid="circle-composer">
      <label className="sr-only" htmlFor="circle-msg">Message</label>
      <textarea id="circle-msg" rows={1} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} placeholder="Write a message" maxLength={2000} className="max-h-40 min-h-[42px] flex-1 resize-y rounded-2xl border border-border bg-background px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E0007A]/40" />
      <Button type="submit" className="h-[42px] rounded-full px-5" disabled={busy || !text.trim()}>Send</Button>
    </form>
  );
}

export function CircleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [c, setC] = useState(undefined);
  const [chan, setChan] = useState(null);
  const [msgs, setMsgs] = useState(undefined);
  const [members, setMembers] = useState(undefined);
  const [tab, setTab] = useState(() => { const t = params.get("tab"); return ["chat", "classroom", "challenges", "leaderboard", "members"].includes(t) ? t : "chat"; });
  const [busy, setBusy] = useState(false);
  usePageMeta(c?.name || "Circle", c?.description || "A Circle on Fable +.");

  const load = () => circlesApi.get(id).then((d) => { setC(d); setChan((ch) => ch || (d.channels || [])[0]?.id || null); }).catch((e) => setC({ error: e?.status === 404 ? "This Circle doesn't exist." : e?.message || "Couldn't load this Circle." }));
  useEffect(() => { setC(undefined); load(); }, [id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!c || c.error || !chan) return;
    setMsgs(undefined);
    circlesApi.messages(id, chan, { limit: 40 }).then((d) => setMsgs(d.items || [])).catch(() => setMsgs([]));
  }, [c, chan, id]);
  useEffect(() => {
    if (!c || c.error || tab !== "members" || members) return;
    circlesApi.members(id, { limit: 60 }).then((d) => setMembers(d.items || d || [])).catch(() => setMembers([]));
  }, [tab, c, id, members]);
  // back from paid join
  useEffect(() => {
    const sid = params.get("circle_session");
    if (!sid) return undefined;
    let tries = 0; let alive = true;
    const poll = async () => {
      try { const r = await circlesApi.checkoutStatus(sid); if (!alive) return; if (r.active || r.joined || r.paid || r.status === "active") { toast.success("You're in."); const n = new URLSearchParams(params); n.delete("circle_session"); setParams(n, { replace: true }); load(); return; } } catch { /* retry */ }
      if (++tries < 12 && alive) setTimeout(poll, 2000);
    };
    poll();
    return () => { alive = false; };
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const join = async () => {
    if (!user) { navigate("/login", { state: { from: `/circles/${id}` } }); return; }
    setBusy(true);
    try {
      if (c.access?.model === "paid") {
        const r = await circlesApi.joinCheckout(id, "month", window.location.origin);
        if (r.url) { window.location.assign(r.url); return; }
      } else {
        const d = await circlesApi.join(c.id, params.get("invite"));
        setC(d); toast.success(`Welcome to ${d.name}`);
      }
    } catch (e) { toast.error(e.message || "Couldn't join."); } finally { setBusy(false); }
  };
  const leave = async () => {
    setBusy(true);
    try { const d = await circlesApi.leave(id); setC(d); toast("You left the Circle."); } catch (e) { toast.error(e.message || "Couldn't leave."); } finally { setBusy(false); }
  };

  if (c === undefined) return <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6"><Skeleton className="h-48 rounded-3xl" /><Skeleton className="mt-4 h-8 w-1/2" /></div>;
  if (c.error) return <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6"><p className="font-semibold">{c.error}</p><Link to="/circles"><Button variant="outline" className="mt-5 rounded-full"><ArrowLeft size={16} className="mr-1.5" /> All Circles</Button></Link></div>;

  const member = c.viewer?.is_member || c.viewer?.role;
  const channels = c.channels || [];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6" data-testid={`circle-detail-${c.id}`}>
      <Link to="/circles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Circles</Link>
      <div className="relative mt-3 h-44 overflow-hidden rounded-3xl bg-muted sm:h-56">{c.cover_url && <img src={c.cover_url} alt="" className="h-full w-full object-cover" />}</div>
      <div className="-mt-10 flex flex-col gap-4 px-2 sm:flex-row sm:items-end sm:px-6">
        <div className="rounded-2xl border-4 border-background bg-background"><CreatorAvatar author={{ avatar_url: c.avatar_url, display_name: c.name }} size={88} className="!rounded-xl" /></div>
        <div className="min-w-0 flex-1 sm:pb-1">
          <h1 className="font-serif-display text-2xl font-bold sm:text-3xl">{c.name}</h1>
          <p className="text-sm text-muted-foreground">{compact(c.stats?.members)} members · {price(c.access)} · run by <Link to={`/@${c.owner?.handle}`} className="font-medium hover:underline">{c.owner?.display_name}</Link> <VerifiedMark author={c.owner} size={12} /></p>
        </div>
        <div className="sm:pb-1">
          {c.viewer?.role === "owner" || c.viewer?.is_mod ? <Link to={"/circles/" + c.id + "/manage"}><Button className="rounded-full px-5" data-testid="manage-btn">Manage</Button></Link>
            : member ? <Button variant="outline" className="rounded-full" onClick={leave} disabled={busy} data-testid="leave-btn">Leave</Button>
            : <Button className="rounded-full px-6" onClick={join} disabled={busy} data-testid="join-btn">{c.access?.model === "paid" ? `Join · ${price(c.access)}` : "Join free"}</Button>}
        </div>
      </div>
      {c.description && <p className="mt-5 max-w-2xl text-[15px] leading-relaxed">{c.description}</p>}
      {c.rules && <p className="mt-2 text-sm text-muted-foreground">Rules: {c.rules}</p>}

      <div className="mt-6 flex gap-1 overflow-x-auto border-b border-border [scrollbar-width:none]" role="tablist">
        {[["chat", "Channels"], ["classroom", "Classroom"], ["challenges", "Challenges"], ["leaderboard", "Leaders"], ["members", "Members"]].map(([k, l]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold ${tab === k ? "border-[#E0007A] text-foreground" : "border-transparent text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "classroom" && <CircleClassroom cid={c.id} member={!!member} />}
      {tab === "challenges" && <CircleChallenges cid={c.id} member={!!member} />}
      {tab === "leaderboard" && <CircleLeaderboard cid={c.id} member={!!member} />}
      {tab === "chat" ? (
        <div className="mt-4 grid gap-4 md:grid-cols-[200px_minmax(0,1fr)]">
          <ul className="flex gap-2 overflow-x-auto md:block md:space-y-1">
            {channels.map((ch) => (
              <li key={ch.id}><button type="button" onClick={() => setChan(ch.id)} className={`w-full whitespace-nowrap rounded-xl px-3 py-2 text-left text-sm ${chan === ch.id ? "bg-foreground text-background" : "hover:bg-muted"}`}># {ch.name}{ch.kind === "announcements" ? " 📣" : ""}</button></li>
            ))}
          </ul>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="circle-messages">
            {msgs === undefined ? <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
              : msgs.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No messages here yet.</p>
              : <ul className="space-y-4">{[...msgs].reverse().map((m) => (
                <li key={m.id} className="flex items-start gap-3">
                  <CreatorAvatar author={m.author} size={32} />
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-1.5 text-sm"><span className="font-semibold">{m.author?.display_name || m.author?.handle}</span><span className="text-xs text-muted-foreground">{timeAgo(m.created_at)}</span>{m.pinned && <Pin size={12} className="text-muted-foreground" />}<CircleMessageTools m={m} canModerate={["owner", "admin", "moderator"].includes(c.viewer?.role)} onChanged={() => circlesApi.messages(id, chan, { limit: 40 }).then((d) => setMsgs(d.items || [])).catch(() => {})} /></p>
                    <p className="whitespace-pre-wrap text-sm">{m.hidden ? <span className="italic text-muted-foreground">Hidden by moderators</span> : m.text}</p>
                  </div>
                </li>
              ))}</ul>}
            {member && (channels.find((x) => x.id === chan)?.kind !== "announcements" || c.viewer?.is_mod) ? <Composer cid={c.id} chan={chan} onSent={(m) => setMsgs((xs) => [m, ...(xs || [])])} /> : <p className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground"><Lock size={13} /> {member ? "Only the host posts in announcements." : "Join to take part in the conversation."}</p>}
          </div>
        </div>
      ) : tab === "members" ? (
        <div className="mt-4" data-testid="circle-members">
          {members === undefined ? <Skeleton className="h-32 rounded-2xl" /> : members.length === 0 ? (
            <p className="flex items-center gap-2 py-8 text-center text-sm text-muted-foreground"><Lock size={14} /> Members are visible to members.</p>
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {members.map((m) => {
                const u = m.member || m.user || m.profile || m;
                return (
                  <li key={m.user_id || m.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-2.5">
                    <CreatorAvatar author={u} size={36} />
                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{u.display_name || u.handle || "Member"}</p><p className="text-xs capitalize text-muted-foreground">{m.role || "member"}{m.points != null ? ` · ${m.points} pts` : ""}</p></div>
                    {u.handle && <Link to={`/@${u.handle}`} className="text-xs font-medium text-primary hover:underline">View</Link>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
      {c.stats?.upcoming_events > 0 && <p className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays size={15} /> {c.stats.upcoming_events} upcoming meetup{c.stats.upcoming_events > 1 ? "s" : ""} — details in the app.</p>}
    </div>
  );
}
